import React, {Fragment, useState} from 'react';
import {observer} from "mobx-react";
import HeightPin from "../HeightPin";
import ScrollSection from "../ScrollSection";
import GemDiscover from "../elements/GemDiscover";
import MoodBoard from "../MoodBoard"
import ImageCirclePack from "../charts/ImageCirclePack";
import TreeMap from "../charts/TreeMap";
import CardFlip from "../elements/CardFlip";
import FormElement from "../elements/FormElement";
import Results from "../elements/Results";
import SectionTop from "./SectionTop";
import DidYouKnow from "../elements/DidYouKnow";
import {getAltText, getSectionImage, getInfoClass, getImageInfoStyle} from "../../assets/settings";

function CampusDesign(props) {
    const {store, state, color, index, downhill} = props;
    const {circlePackStore} = store;

    const group = 'CD';
    const activeKey = store.activeSection(group) || 'CD_2';


    const sectionY = 0.35;


    return (
        <div className={'Section'}>
            <SectionTop group={group} downhill={downhill}>
                <h4>Campus design is the physical organizing framework for a university campus.</h4>
                <p>
                    Organizing elements include buildings, open spaces and natural features, topography, and mobility
                    networks.
                </p>
                <p>
                    Achieving the appropriate balance between these organizing elements will help establish a cohesive
                    and intuitive structure for the campus. These design elements affect how people navigate and
                    experience the campus.
                </p>
                <p>
                    The planning process presents an opportunity to learn from prior campus design decisions and propose
                    ideas that reconcile the past with present and future space needs.
                </p>
                <p className="photocredit">
                    Photo Credit: California Digital Library
                </p>
            </SectionTop>
            <HeightPin store={store} state={state} scrollChild={<div className={'left-scroll'}>
                <div className={'spacer'} style={{height: 70}}/>

                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_2`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_2')}>
                        <h4>How has the campus evolved over time?</h4>
                        <p>
                            Berkeley’s development history is reflected in several visionary plans that were each
                            partially implemented. Many of these plans tried to impose order on natural systems.
                        </p>
                        <p>
                            The most notable campus plan, still evident today, was implemented by campus architect John
                            Galen Howard between 1914 and 1927. This plan integrated the campus’s picturesque landscape
                            (a landscape style prioritizing the natural environment) with the Beaux Arts, classical
                            style of the early 20th century.
                        </p>
                        <DidYouKnow store={store} color={color}>
                            <p>
                                Did you know that the Berkeley campus is oriented towards the Golden Gate? This
                                east-west axis was first proposed in the 1865 Olmsted Plan, which established the
                                campus’s picturesque landscape.
                            </p>
                        </DidYouKnow>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_4`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_4')}>
                        <h4>What are the predominant building uses on campus?</h4>
                        <p>
                            Most Campus Park buildings house research labs and office space. Less overall building space
                            is allocated to study spaces, athletics and recreation space, and student services space.
                        </p>
                        <p>
                            These proportions reflect campuswide space needs as well as technical space requirements and
                            metrics.
                        </p>

                        <p>
                            Residential space is located outside of the Campus Park within the City of Berkeley, and at
                            the Clark Kerr Campus to the southeast.
                        </p>

                        <DidYouKnow store={store} color={color}>
                            <p>
                                Did you know? Classrooms and teaching labs (such as art studios, physical science labs,
                                etc.) make up about 5% of all existing space on campus.
                            </p>
                        </DidYouKnow>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_5`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_5')}>
                        <h4>How is the campus defined through landscape?</h4>
                        <p>
                            The campus is well-known for its park-like character.
                        </p>
                        <p>
                            The topography and two major landscape features define the structure of Campus Park:
                            Strawberry Creek and a series of open glades dispersed through the campus core.
                        </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_6`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_6')}>
                        <p>
                            UC Berkeley’s outdoor athletics and recreation fields are located in the south-west area of
                            the Campus Park, within the Strawberry Canyon recreation area near California Memorial
                            Stadium, and on the Clark Kerr Campus.
                        </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_7`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_7')}>
                        <p>
                            Plazas and courtyards throughout campus are used as places to gather, have outdoor events,
                            and work outside. The main campus plazas are Sproul Plaza and Lower Sproul Plaza, though
                            many buildings have smaller gathering places at their entrances.
                        </p>
                        <p>
                            Courtyards are typically more intimate than plazas and are largely found in residential
                            areas in the Foothill-Stern site, in the Southside, and on Clark Kerr Campus.
                        </p>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_8`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_8')}>
                        <p>
                            There are two primary gateways to the Campus Park: Sproul Plaza along Bancroft Way to the
                            south and an open lawn at the historic Springer Gateway along Oxford Street to the west.
                        </p>
                        <p>
                            Major pedestrian pathways include a north-south route extending from Sproul Plaza to Moffitt
                            Library and North Gate, and an east-west connection extending from the Eucalyptus Grove to
                            Sather Tower.
                        </p>
                        <p>
                            <b>The variety of open spaces are an important resource to the campus community and
                                neighbors, serving as a park-like place for relaxation and contemplation.</b>
                        </p>

                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_9`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_9')}>
                        <h4>What’s the balance of open space to buildings today?</h4>
                        <p>
                            The majority of the Campus Park is occupied by impervious surfaces - harder surfaces that
                            don’t absorb water, like buildings and paved pathways.
                        </p>
                        <p>
                            The remaining areas of campus are covered in pervious surfaces - softer surfaces that absorb
                            water when it rains, including lawns, planted areas, some types of paving, and other
                            landscapes.
                        </p>

                        <DidYouKnow store={store} color={color}>
                            <p>
                                UC Berkeley’s current design standards require no net new stormwater runoff when
                                projects are implemented. Stormwater runoff is modeled as projects are designed -- if
                                there is a net increase in runoff, it must be mitigated through stormwater management
                                strategies, such as bioswales.
                            </p>
                        </DidYouKnow>
                    </div>
                </ScrollSection>
                <ScrollSection store={store} sectionY={sectionY} background={'none'} sectionGroup={group}
                               sectionKey={`CD_10`}>
                    <div style={getImageInfoStyle(color)} className={getInfoClass(activeKey, 'CD_10')}>
                        <p>
                            This map shows what areas of the campus are pervious. Pervious surfaces provide open space
                            relief throughout the campus and facilitate stormwater management. Campus open spaces
                            contribute to the overall campus experience and can also aid wayfinding.
                        </p>
                        <p>
                            Providing sufficient space to accommodate programs while retaining a cohesive campus fabric
                            through building and landscape design is critical to the planning process.
                        </p>
                    </div>
                </ScrollSection>
                <div className={'spacer'} style={{height: '50vh'}}/>
            </div>}
                       pinChild={<div className={'sticky-image-right'}>
                           <div className={'frame'}>
                               {getAltText(activeKey).title && <h4 style={{'backgroundColor': color + '80'}}>{getAltText(activeKey).title}</h4>}
                               <img width={'100%'} src={getSectionImage(group, activeKey)}
                                    alt={getAltText(activeKey).alt}/>
                           </div>
                       </div>}
            />
            <div className={'interactive'}>
                <CardFlip color={color} index={index} store={store}
                          frontFace={<FormElement store={store} id={group}/>}
                          backFace={<Fragment>
                              <Results store={store} id={group}/>
                              {/*<GemDiscover store={store} color={color} index={index}/>*/}

                          </Fragment>}/>
            </div>
        </div>
    );
}

export default observer(CampusDesign);
